import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import qs from 'qs';
import { LOCATION_CHANGE, LocationChangeAction } from 'connected-react-router';

import { IUser } from '../users/user';
import { RootState } from '../rootReducer';
import loginReducers from './login.reducers';
import refreshTokenReducers from './refreshToken.reducers';
import { reducers as passwordReducers } from './passwordActions.reducers';
import { reducers as updateUserCredentials } from './updateProspectCredentials.reducers';
import { PayloadTypes } from '../../services/api';
import { AuthState, initialState, LoggingMode } from './authState';
import { UserTypes } from '../../components/common/domain/userTypes';
import { UserRoles } from '../../components/common/domain/userRoles';
import { AccessTypes } from '../../components/common/domain/accountAccessType';
import { SX_ADMIN_ENABLED_ACCOUNTS_IDS } from '../../config';
import { isAccountAdmin } from '../../utils/user';
import { HttpCallStatus } from './login.thunk.interfaces';
import { ILoginOptionState } from './login.interfaces';
import { getEmptyArray } from '../utils';

const REDIRECTABLE_PATHS: ReadonlyArray<string> = ['/login', '/signUp', '/idpresponse', '/twoFactorAuth'];

const getLoggedInUser = (state: RootState): IUser | null => state.auth.user;
const getAuthToken = (state: RootState): string => state.auth.token;
const getLoadingAuthState = (state: RootState): boolean => state.auth.loading;
const getIsLoggedInState = (state: RootState): boolean => state.auth.isLoggedIn;
const getLoggedInUserId = (state: RootState): string | null => state.auth.userId;
const getUniqueSession = (state: RootState): boolean => state.auth.uniqueSession;
const getDisplayImpersonateModal = (state: RootState): boolean => state.auth.displayImpersonateModal;
const getOriginalUser = (state: RootState): IUser | null => state.auth.originalUser;
const getLoadingImpersonate = (state: RootState): boolean => state.auth.loadingImpersonate;
const getAuthError = (state: RootState): string | null => state.auth.error;
const getAuthHash = (state: RootState): string | null => state.auth.hash;
const is2faEnabled = (state: RootState): boolean => state.auth.payloadType === PayloadTypes.TwoFactorRequired;
const getLoggingMode = (state: RootState): LoggingMode => state.auth.loggingMode;
const getRedirectUrl = (state: RootState): string => state.auth.redirectUrl;
const getWebClientId = (state: RootState): string | null => state.auth.webClientId;
const getAccountUsage = (state: RootState): number | null | undefined => state.auth.accountUsage;
const getAuthAccountAccessType = (state: RootState): AccessTypes | undefined => state.auth.user?.account.accessType;
const isAuthUserAdminOrOwner = (state: RootState): boolean => !!(state.auth.user &&
  (state.auth.user.userType === UserTypes.AccountAdmin || state.auth.user.roles?.includes(UserRoles.CompanyOwner)));
const isAuthUserSightxAdmin = (state: RootState): boolean => !!(state.auth.user && state.auth.user.userType === UserTypes.SxAdmin);
const getUpdateProspectCredentialsShowRecaptchaV2 = (state: RootState): boolean => state.auth.updateProspectCredentials.showReCaptchaV2;
const getShowReCaptchaV2ForLogin = (state: RootState): boolean => !!state?.auth?.showReCaptchaV2ForLogin;
const isCreditPoolAdmin = (state: RootState): boolean => {
  const user = state.auth?.user;
  const userType = user?.userType;
  const accountId = user?.accountId;
  if (!userType || !accountId) {
    return false;
  }

  if (UserTypes.SxAdmin === userType) {
    return true;
  }

  const isAccountInsideBlackList = SX_ADMIN_ENABLED_ACCOUNTS_IDS.has(accountId);
  if (isAccountInsideBlackList) {
    return false;
  }

  if (UserTypes.MainAdmin === userType) {
    return true;
  }

  return UserTypes.AdminAssistant === userType && !!user?.managedAccounts?.includes(accountId);
};
const isCreditPoolUserFacing = (state: RootState): boolean => {
  const user = state.auth?.user;
  const userType = user?.userType;
  return isAccountAdmin(userType);
};

// POC TODO. add tests
const selectRepdataSSOLoginStatus = (state: RootState): HttpCallStatus => {
  return state.auth.repdataSSOLoginStatus;
};

// POC TODO. add tests
const selectFetchWhoAmIStatus = (state: RootState): HttpCallStatus => {
  return state.auth.fetchWhoAmIStatus;
};

// POC TODO. add tests
const selectFetchLoginOptionsStatus = (state: RootState): HttpCallStatus => {
  return state.auth.fetchLoginOptionsStatus;
};

// POC TODO. add tests
const selectLoginOptions = (state: RootState): ILoginOptionState[] => {
  return state.auth.loginOptions || getEmptyArray();
};

export const selectors = {
  getLoggedInUser,
  getLoadingAuthState,
  getIsLoggedInState,
  getLoggedInUserId,
  getUniqueSession,
  getAuthToken,
  getDisplayImpersonateModal,
  getOriginalUser,
  getLoadingImpersonate,
  getAuthError,
  getAuthHash,
  is2faEnabled,
  getLoggingMode,
  getRedirectUrl,
  getWebClientId,
  getAccountUsage,
  isAuthUserAdminOrOwner,
  getAuthAccountAccessType,
  isAuthUserSightxAdmin,
  getUpdateProspectCredentialsShowRecaptchaV2,
  getShowReCaptchaV2ForLogin,
  isCreditPoolAdmin,
  isCreditPoolUserFacing,
  selectRepdataSSOLoginStatus,
  selectFetchWhoAmIStatus,
  selectFetchLoginOptionsStatus,
  selectLoginOptions
};

export type ImpersonateUserPayload = {
  impersonatedUser: IUser;
  impersonatedToken: string;
}

export type SetRedirectURLPayload = {
  url: string;
}

export type AccountUsagePayload = {
  usage: number;
}

const auth = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuthToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
    },
    updateUniqueSession: (state) => {
      state.uniqueSession = false;
    },
    startLogoutSession: (state) => {
      state.isLoggedIn = false;
    },
    logoutSession: (state) => {
      state.token = '';
      state.isLoggedIn = false;
      state.error = null;
      state.userId = null;
      state.user = null;
      state.uniqueSession = true;
      state.payloadType = null;
      state.hash = null;
    },
    setImpersonatedUser: (state, action: PayloadAction<ImpersonateUserPayload>) => {
      state.originalUser = state.user;
      state.originalToken = state.token;
      state.userId = action.payload.impersonatedUser.id;
      state.user = action.payload.impersonatedUser;
      state.token = action.payload.impersonatedToken;
    },
    clearImpersonatedUser: (state) => {
      if (state.originalUser) {
        state.user = state.originalUser;
        state.userId = (state.originalUser as IUser).id;
        state.originalUser = null;
      }
      state.token = state.originalToken;
      state.originalToken = '';
    },
    deleteRedirectUrl: (state) => {
      state.redirectUrl = '';
    },
    setRedirectUrl: (state, action: PayloadAction<SetRedirectURLPayload>) => {
      state.redirectUrl = action.payload.url;
    },
    setIsLoggedIn: (state) => {
      state.isLoggedIn = true;
    },
    setWebClientId: (state, action: PayloadAction<string | null>) => {
      state.webClientId = action.payload;
    },
    setAccountUsage: (state, action: PayloadAction<number>) => {
      state.accountUsage = action.payload;
    },
    cleanUpdateProspectCredentialsShowReCaptchaV2: (state) => {
      state.updateProspectCredentials.showReCaptchaV2 = false;
    },
    clearShowReCaptchaV2ForLogin: (state) => {
      state.showReCaptchaV2ForLogin = false;
    },
    clearLoginOptions: (state) => {
      state.loginOptions = [];
    }
  },
  extraReducers: {
    ...loginReducers,
    ...refreshTokenReducers,
    ...passwordReducers,
    ...updateUserCredentials,
    [LOCATION_CHANGE]: (state: AuthState, action: LocationChangeAction): void => {
      const { pathname, search: queryParamsRaw } = action.payload.location;
      if (REDIRECTABLE_PATHS.includes(pathname)) {
        const parsedQueryParams = qs.parse(queryParamsRaw, { ignoreQueryPrefix: true });
        const redirectUrl = parsedQueryParams['redirectUrl'] as string;
        if (redirectUrl) {
          state.redirectUrl = redirectUrl;
        }
      } else {
        state.redirectUrl = '';
      }
    }
  }
});

export const {
  setAuthToken,
  updateUniqueSession,
  startLogoutSession,
  logoutSession,
  setImpersonatedUser,
  clearImpersonatedUser,
  deleteRedirectUrl,
  setRedirectUrl,
  setIsLoggedIn,
  setWebClientId,
  setAccountUsage,
  cleanUpdateProspectCredentialsShowReCaptchaV2,
  clearShowReCaptchaV2ForLogin,
  clearLoginOptions
} = auth.actions;

export const caseReducers = auth.caseReducers;
export default auth.reducer;
